import React, { useEffect, useMemo, useState } from 'react';
import SlidingSideBar from '../../../SlidingSideBar/SlidingSideBar';
import { Button, Spinner } from 'react-bootstrap';
import isEqual from 'lodash/isEqual';
import { ENDPOINTS, ORDER_TYPES, BACKEND_FIELDS_TO_EXCLUDE } from '../../../../helpers/constants';
import { makeRESTApiRequest } from '../../../../helpers/api';
import Loader from '../../../Loader';
import { toast } from 'sonner';
import useReportGeneration from '../../../../hooks/useReportGeneration';
import ReportFormGenerator from '../../../reports/FormGenerator';
import StepTabNavigation from '../../../reports/StepTabNavigation';
import ReportModals from '../../../reports/ReportModals';
import { autoFillInvoicesFromFiles, handleInvoiceMultiFieldChange, getInvoiceMultiFields, EMPTY_INVOICE_ROW, computeSuggestedInvoiceAmount, isInvoiceTotalBelowSuggested } from '../../../reports/utils/helpers';
import { getFullAddress } from '../../../../helpers/global';

const getFormSteps = isFilePopupOpen => {
  return [
    {
      key: 'invoices',
      name: 'Invoices',
      isComplete: false,
      cellSize: 12,
      fields: [
        {
          label: 'Fees',
          hideLabel: true,
          type: 'fixed-fields',
          cellSize: 12,
          groupKey: 'invoices',
          fields: [
            {
              label: 'Hard Cost',
              key: 'hardCost',
              type: 'currency-input',
              hideLabel: true,
              cellSize: 12,
              cellSizeMd: 2,
              groupKey: 'invoices'
            },
            {
              label: 'Processing Fee',
              key: 'processingFee',
              type: 'currency-input',
              hideLabel: true,
              cellSize: 12,
              cellSizeMd: 2,
              groupKey: 'invoices'
            },
            {
              label: 'Suggested Invoice Amount',
              key: 'suggestedInvoiceAmount',
              type: 'static',
              displayAs: 'currency',
              cellSize: 12,
              cellSizeMd: 2,
              note: values => {
                const min = Number(values?.minimumInvoicableAmount) || 0;
                const formatted = `$${min.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}`;
                return `Note: Minimum invoicable amount is ${formatted}.`;
              },
              groupKey: 'invoices',
              staticTone: values => (isInvoiceTotalBelowSuggested(values) ? 'error' : 'default'),
              staticTooltip: values => {
                if (!isInvoiceTotalBelowSuggested(values)) return null;
                const suggested = Number(values?.suggestedInvoiceAmount) || 0;
                const totalInvoiceAmount = (values?.invoices || []).reduce(
                  (sum, row) => sum + (Number(row?.invoiceAmount) || 0),
                  0
                );
                return `Suggested invoice amount ($${suggested}) is less than total invoice amount ($${totalInvoiceAmount}).`;
              },
              showStaticIcon: values => isInvoiceTotalBelowSuggested(values)
            }
          ]
        },
        {
          label: 'Invoices',
          key: 'invoices',
          hideLabel: true,
          type: 'multi-field',
          cellSize: 12,
          groupKey: 'invoices',
          className: 'px-4',
          fields: getInvoiceMultiFields({ isFilePopupOpen })
        },
        {
          label: 'Invoice Documents',
          key: 'invoicesFiles',
          type: 'file',
          cellSize: 12
        }
      ]
    }
  ];
};

const defaultValues = {
  invoices: {
    invoices: [{ ...EMPTY_INVOICE_ROW }],
    isComplete: false,
    hardCost: null,
    processingFee: null,
    minimumInvoicableAmount: null,
    suggestedInvoiceAmount: null
  },
  documents: {
    isComplete: false,
    invoicesFiles: []
  }
};

const hydrateSurveyInvoiceFees = (invoices = {}, surveyData) => {
  const root = surveyData?.[0] || surveyData || {};
  const surveyOrder =
    (root?.orders || []).find(order => order?.orderType === 'Survey') ||
    (root?.orderType === 'Survey' ? root : {});
  const organization = root?.organization || {};

  const hardCost = surveyOrder.hardCost ?? invoices.hardCost ?? 0;
  const processingFee =
    surveyOrder.processingFee ?? organization.surveyProcessingFee ?? invoices.processingFee ?? 0;
  const minimumInvoicableAmount =
    organization.surveyMinimumInvoicableAmount ?? invoices.minimumInvoicableAmount ?? 0;

  return {
    ...invoices,
    hardCost,
    processingFee,
    minimumInvoicableAmount,
    suggestedInvoiceAmount: computeSuggestedInvoiceAmount(
      hardCost,
      processingFee,
      minimumInvoicableAmount
    )
  };
};

const buildFormStateFromSurveyReport = (report, surveyData = null) => {
  if (!report) return null;

  const mergedValues = JSON.parse(JSON.stringify(defaultValues));
  const reportInvoices = report.invoices;

  if (reportInvoices && Array.isArray(reportInvoices.invoices) && reportInvoices.invoices.length) {
    mergedValues.invoices = {
      invoices: reportInvoices.invoices.map(inv => ({
        ...(inv._id != null && { _id: String(inv._id) }),
        ...(inv.serviceVersionId != null && {
          serviceVersionId: String(inv.serviceVersionId)
        }),
        invoiceNumber: inv.invoiceNumber ?? '',
        invoiceAmount: inv.invoiceAmount ?? '',
        paymentReceived: inv.paymentReceived ?? '',
        invoiceDueDate: inv.invoiceDueDate ?? '',
        status: inv.status || 'Unpaid'
      })),
      ...(reportInvoices.isComplete !== undefined && { isComplete: reportInvoices.isComplete })
    };
  }

  if (report.invoices?.isComplete !== undefined) {
    mergedValues.invoices.isComplete = !!report.invoices.isComplete;
  }

  mergedValues.invoices = hydrateSurveyInvoiceFees(mergedValues.invoices, surveyData);
  mergedValues.documents.invoicesFiles = Array.isArray(reportInvoices?.files) ? reportInvoices.files : [];

  return { mergedValues };
};

const AddEditSurveyReportSidebar = ({
  show,
  onHide,
  survey,
  loading,
  service,
  onAfterSave,
  surveyOrderId
}) => {
  const [step, setStep] = useState(0);
  const [driveFiles, setDriveFiles] = useState(null);
  const [driveFilesLoading, setDriveFilesLoading] = useState(false);
  const [formValues, setFormValues] = useState(() => {
    const built = buildFormStateFromSurveyReport(survey?.report, survey);
    return built?.mergedValues || JSON.parse(JSON.stringify(defaultValues));
  });
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [validationFieldErrors, setValidationFieldErrors] = useState({});
  const [confirmationModal, setConfirmationModal] = useState(null);
  const [showUnsavedChangesAlert, setShowUnsavedChangesAlert] = useState(false);
  const [initialFormState, setInitialFormState] = useState(null);

  const reportPropertyAddress = useMemo(
    () => getFullAddress(service) || '',
    [service]
  );

  const formSteps = useMemo(() => getFormSteps(false), []);

  const cleanFormStateForComparison = state => {
    const cleaned = JSON.parse(JSON.stringify(state));
    BACKEND_FIELDS_TO_EXCLUDE.forEach(field => {
      delete cleaned[field];
    });
    return cleaned;
  };

  const updateInitialFormState = () => {
    const cleanedFormValues = cleanFormStateForComparison(formValues);
    setInitialFormState({ formValues: cleanedFormValues });
  };

  const onReportGenerationSuccess = savedReport => {
    if (savedReport) {
      const built = buildFormStateFromSurveyReport(savedReport, service);
      if (built) {
        setFormValues(built.mergedValues);
        setInitialFormState({ formValues: cleanFormStateForComparison(built.mergedValues) });
      }
    } else {
      updateInitialFormState();
    }
    onAfterSave?.();
  };

  const checkForUnsavedChanges = () => {
    if (!initialFormState) return false;
    const cleanedCurrentFormValues = cleanFormStateForComparison(formValues);
    return !isEqual(cleanedCurrentFormValues, initialFormState.formValues);
  };

  const handleClose = () => {
    if (checkForUnsavedChanges()) {
      setShowUnsavedChangesAlert(true);
    } else {
      onHide();
    }
  };

  const handleSaveAndContinue = async () => {
    const saveResult = await onSaveReport();
    if (saveResult === true) {
      setShowUnsavedChangesAlert(false);
      onHide();
    } else {
      setShowUnsavedChangesAlert(false);
    }
  };

  const handleDiscardChanges = () => {
    setShowUnsavedChangesAlert(false);
    onHide();
  };

  const handleCancelAction = () => {
    setShowUnsavedChangesAlert(false);
  };

  const handleToggleStepCompletion = (sectionKey, isComplete) => {
    setFormValues(prev => ({
      ...prev,
      [sectionKey]: {
        ...(prev[sectionKey] || {}),
        isComplete: isComplete
      }
    }));
  };

  const loadDriveFiles = async folderId => {
    setDriveFilesLoading(true);
    const { response, error } = await makeRESTApiRequest({
      method: 'GET',
      endpoint: `${ENDPOINTS.GOOGLE_DRIVE_READ_FOLDER}?folderId=${folderId}`
    });
    setDriveFilesLoading(false);
    if (error) {
      toast.error('Failed to load drive files');
      return;
    }
    setDriveFiles(response);
  };

  useEffect(() => {
    if (survey?.report) {
      const built = buildFormStateFromSurveyReport(survey.report, survey);
      if (!built) return;
      setFormValues(built.mergedValues);
      setInitialFormState({ formValues: cleanFormStateForComparison(built.mergedValues) });
    }
  }, [survey]);

  useEffect(() => {
    const handleBeforeUnload = e => {
      if (show && checkForUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [show, formValues, initialFormState]);

  const onTabSelect = key => {
    const index = formSteps.findIndex(s => s.key === key);
    if (index !== -1) setStep(index);
  };

  const prepareReportData = () => {
    const reportData = {};
    if (formValues.invoices) {
      const rawInvoices = formValues.invoices.invoices || [];
      reportData.invoices = {
        invoices: rawInvoices.map(inv => ({
          ...(inv._id != null && { _id: inv._id }),
          ...(inv.serviceVersionId != null && { serviceVersionId: inv.serviceVersionId }),
          invoiceNumber: inv.invoiceNumber || '',
          invoiceAmount:
            inv.invoiceAmount !== undefined && inv.invoiceAmount !== null && inv.invoiceAmount !== ''
              ? Number(inv.invoiceAmount)
              : undefined,
          paymentReceived:
            inv.paymentReceived !== undefined && inv.paymentReceived !== null && inv.paymentReceived !== ''
              ? Number(inv.paymentReceived)
              : undefined,
          invoiceDueDate: inv.invoiceDueDate || undefined,
          status: inv.status || 'Unpaid'
        })),
        files: formValues.documents?.invoicesFiles || [],
        hardCost:
          formValues.invoices.hardCost !== undefined &&
          formValues.invoices.hardCost !== null &&
          formValues.invoices.hardCost !== ''
            ? Number(formValues.invoices.hardCost)
            : undefined,
        processingFee:
          formValues.invoices.processingFee !== undefined &&
          formValues.invoices.processingFee !== null &&
          formValues.invoices.processingFee !== ''
            ? Number(formValues.invoices.processingFee)
            : undefined,
        minimumInvoicableAmount:
          formValues.invoices.minimumInvoicableAmount !== undefined &&
          formValues.invoices.minimumInvoicableAmount !== null &&
          formValues.invoices.minimumInvoicableAmount !== ''
            ? Number(formValues.invoices.minimumInvoicableAmount)
            : undefined,
        suggestedInvoiceAmount: computeSuggestedInvoiceAmount(
          formValues.invoices.hardCost,
          formValues.invoices.processingFee,
          formValues.invoices.minimumInvoicableAmount
        ),
        isComplete: !!formValues.invoices.isComplete
      };
    }
    return reportData;
  };

  const { isSaving, onSaveReport } = useReportGeneration({
    endpoints: {
      save: ENDPOINTS.SURVEY_REPORT_WITH_ID,
      generate: ENDPOINTS.GENERATE_SURVEY_REPORT
    },
    getEntityId: () => surveyOrderId,
    getReportId: () => survey?.report?._id || survey?.reportId,
    prepareReportData,
    fileInformation: {},
    onSuccess: onReportGenerationSuccess,
    onValidationError: ({ fieldErrors = {} } = {}) => setValidationFieldErrors(fieldErrors),
    downloadFilePrefix: 'Survey_Report'
  });

  const validationFieldStatuses = useMemo(() => {
    const keys = validationFieldErrors[formSteps[step]?.key] || [];
    return Object.fromEntries(keys.map(key => [key, 'error']));
  }, [validationFieldErrors, formSteps, step]);

  useEffect(() => {
    if (!show) {
      setDriveFiles(null);
      setInitialFormState(null);
      setValidationFieldErrors({});
      return;
    }
    if (show && service?.driveFolder && !driveFiles && !driveFilesLoading) {
      loadDriveFiles(service.driveFolder);
    }
  }, [show, service?.driveFolder]);

  return (
    <>
      <SlidingSideBar
        fullScreen
        visible={show}
        onClose={handleClose}
        title={`${survey?.reportId ? 'Update' : 'Start'} Report`}
        contentStyle={{ position: 'relative' }}
        dock="left"
      >
        {loading ? (
          <div className="d-flex justify-content-start align-items-center">
            <Loader />
          </div>
        ) : (
          <>
            <div className="p-0 mt-4 mb-5">
              <StepTabNavigation
                formSteps={formSteps}
                currentStep={step}
                formValues={formValues}
                onTabSelect={onTabSelect}
                onToggleStepCompletion={handleToggleStepCompletion}
              />
              <div className="p-2 px-4">
                {show && formSteps[step] && (
                  <ReportFormGenerator
                    orderType={ORDER_TYPES.SURVEY}
                    defaultValues={defaultValues}
                    field={formSteps[step].key}
                    fields={formSteps[step].fields}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    service={service}
                    isFilePopupOpen={false}
                    driveFiles={driveFiles}
                    driveFilesLoading={driveFilesLoading}
                    onUpdateDriveFiles={setDriveFiles}
                    setConfirmationModal={setConfirmationModal}
                    showValidationErrors={showValidationErrors}
                    setValidationStatus={setShowValidationErrors}
                    enableFieldStatus={Object.keys(validationFieldStatuses).length > 0}
                    fieldStatuses={validationFieldStatuses}
                    handleRefresh={() => {
                      if (service?.driveFolder) loadDriveFiles(service.driveFolder);
                    }}
                    onFieldValueChange={({ field, key, value, formValues: fv, setFormValues: setFv }) => {
                      if (key === 'invoicesFiles') {
                        autoFillInvoicesFromFiles({
                          reportPropertyAddress,
                          value,
                          formValues: fv,
                          setFormValues: setFv,
                          reportType: 'Survey'
                        });
                        return true;
                      }

                      if (
                        field === 'invoices' &&
                        ['hardCost', 'processingFee', 'minimumInvoicableAmount'].includes(key)
                      ) {
                        const newFormValues = { ...fv };
                        if (!newFormValues.invoices) newFormValues.invoices = {};
                        newFormValues.invoices[key] = value;

                        const currentInvoices = newFormValues.invoices;
                        const hardCost = Number(currentInvoices.hardCost) || 0;
                        const processingFee = Number(currentInvoices.processingFee) || 0;
                        const minimumInvoicableAmount = Number(currentInvoices.minimumInvoicableAmount) || 0;

                        currentInvoices.suggestedInvoiceAmount = computeSuggestedInvoiceAmount(
                          hardCost,
                          processingFee,
                          minimumInvoicableAmount
                        );

                        setFv(newFormValues);
                        return true;
                      }

                      return false;
                    }}
                    onMultiFieldChange={handleInvoiceMultiFieldChange}
                  />
                )}
              </div>
            </div>

            <div className="bg-white w-100 pb-3 px-2" style={{ position: 'absolute', bottom: 0, right: 0 }}>
              <hr className="mt-0 mb-2 p-0" />
              <div className="d-flex justify-content-end">
                <Button
                  variant="outline-light text-dark border-dark-grey"
                  onClick={onSaveReport}
                  disabled={isSaving}
                >
                  {isSaving && <Spinner size="sm" animation="border" variant="dark" className="mr-1" />}
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </>
        )}

        <ReportModals
          confirmationModal={confirmationModal}
          setConfirmationModal={setConfirmationModal}
          showUnsavedChangesAlert={showUnsavedChangesAlert}
          handleCancelAction={handleCancelAction}
          handleDiscardChanges={handleDiscardChanges}
          handleSaveAndContinue={handleSaveAndContinue}
          isSaving={isSaving}
        />
      </SlidingSideBar>
    </>
  );
};

export default AddEditSurveyReportSidebar;
